import React, {useCallback, useMemo} from 'react';
import {Button, Stack} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import {Neighbourhood, NewJoiner, NewJoinerType} from '../../api/Model';
import {useNewJoinerStore} from '../../api/NewJoinerStore';
import {Page, StorePageCount} from '../../components/page/Page';
import {LoadingData} from '../../components/loading-data/LoadingData';
import {useFormDialogAction} from '../../components/form-dialog/FormDialogProvider';
import {useDeleteAction} from '../../util/actions/DeleteAction';
import {NewJoinerDialog, newJoinerToForm} from './NewJoinerDialog';
import {NewJoinerTable} from './NewJoinerTable';
import {applyNewJoinersFilter, NewJoinersFilter, newJoinersFilterSerializer} from './NewJoinersFilter';
import {useEmployeeNeighbourhoodStore} from "../../api/EmployeeNeighbourhoodStore";
import {ResolvedNewJoiner, useResolvedNewJoinerStore} from "../../api/ResolvedNewJoinerStore";
import {emptyEmployee} from "../../api/Empty";
import {HierarchyEmployee} from "../../util/EmployeeHierarchy";
import {
    GuessNeighbourhoodForm,
    GuessNeighbourhoodPicker
} from "../../components/guess-neighbourhood-dialog/GuessNeighbourhoodPicker";
import {SetState} from "../../util/FormHandlers";
import {NewJoinerApi} from "./NewJoinerApi";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {useMessage} from "../../util/actions/Message";
import {useNewJoinerChecklistItemStore} from "../../api/NewJoinerChecklistItemStore";
import {
    ChecklistManagementForm,
    ChecklistManagementResetButton,
    createNewJoinerChecklistForm,
    saveNewJoinerChecklistForm
} from "./ChecklistManagementForm";
import {ImportBerlinNewJoinersButton} from "./ImportBerlinNewJoinersButton";
import {useFilter} from "../../util/FilterSerializer";
import {RemoteValue} from "../../util/RemoteValue";
import {usePermission} from "../../util/access/UseAccess";
import {
    KeyData_Operate,
    Neighbourhoods_Admin,
    NewJoinerChecklistItems_Admin,
    NewJoiners_Admin
} from "../../util/access/Permission";
import {toEmployeeRef} from "../../api/Projections";

export function NewJoinersPage({neighbourhoods}: {
    neighbourhoods: Neighbourhood[],
}) {
    const {
        createNewJoiner,
        updateNewJoiner,
        deleteNewJoiner,
    } = useNewJoinerStore();

    const newJoiners = useResolvedNewJoinerStore();

    const employeeNeighbourhood = useEmployeeNeighbourhoodStore(state => state.remoteValue);

    const {addEmployee, removeEmployee} = useNeighbourhoodStore();

    const {
        remoteValue: newJoinerChecklistItems,
        createNewJoinerChecklistItem,
        updateNewJoinerChecklistItem,
        deleteNewJoinerChecklistItem,
    } = useNewJoinerChecklistItemStore();

    const filterSerializer = useMemo(
        () => newJoinersFilterSerializer(neighbourhoods),
        [neighbourhoods],
    );
    const [filter, setFilter] = useFilter(filterSerializer);

    const message = useMessage();
    const {canAdmin, canImport, canManageChecklist, canOperateKeys, canManageNeighbourhoods} = usePermission({
        canAdmin: NewJoiners_Admin,
        canImport: NewJoiners_Admin,
        canManageChecklist: NewJoinerChecklistItems_Admin,
        canOperateKeys: KeyData_Operate,
        canManageNeighbourhoods: Neighbourhoods_Admin,
    });

    const handleCreate = useFormDialogAction({
        title: 'Add New Joiner',
        description: 'Enter the details for the new joiner.',
        submitButtonText: 'Add',
        fullWidth: true,
        renderForm: NewJoinerDialog,
        action: openDialog => openDialog({
            initialValue: {
                person: {type: 'existing', employee: emptyEmployee},
                joinDate: '',
                type: NewJoinerType.NewFTE,
                remote: false,
                notes: null,
                checklist: {},
            },
            onSubmit: createNewJoiner,
            successMessage: 'New joiner added',
            errorMessage: 'Failed to add new joiner. Please try again later.',
        }),
    }, [createNewJoiner]);

    const handleEdit = useFormDialogAction({
        title: 'Edit New Joiner',
        description: 'Update the new joiner information below.',
        submitButtonText: 'Save',
        fullWidth: true,
        renderForm: NewJoinerDialog,
        action: (openDialog, newJoiner: NewJoiner) => openDialog({
            initialValue: newJoinerToForm(newJoiner),
            onSubmit: form => updateNewJoiner(newJoiner, form),
            successMessage: 'New joiner updated',
            errorMessage: 'Failed to update new joiner. Please try again later.',
        }),
    }, [updateNewJoiner]);

    const handleDelete = useDeleteAction({
        description: 'Are you sure you want to remove this new joiner? This action cannot be undone.',
        action: deleteNewJoiner,
        successMessage: 'New joiner removed',
        failureMessage: 'Failed to remove new joiner. Please try again later.',
    }, [deleteNewJoiner]);

    const handleSelectNeighbourhood = useFormDialogAction({
        title: 'Guess Neighbourhood',
        description: 'Identify the best matching neighbourhood for the new joiner.',
        submitButtonText: 'Select',
        fullWidth: true,
        renderForm: ({form, setForm}: { form: GuessNeighbourhoodForm, setForm: SetState<GuessNeighbourhoodForm> }) => (
            <GuessNeighbourhoodPicker
                form={form}
                setForm={setForm}
                employeeNeighbourhoods={employeeNeighbourhood}
            />
        ),
        action: (openDialog, employee: HierarchyEmployee) => openDialog({
            initialValue: {employee},
            onSubmit: async form => {
                const neighbourhood = form.neighbourhood;
                if (!neighbourhood) throw new Error('Please select a neighbourhood');
                await addEmployee(form.neighbourhood as Neighbourhood, toEmployeeRef(form.employee));
            },
            successMessage: form => `Added ${employee.fullName} to ${form.neighbourhood?.name}`,
            errorMessage: 'Cannot add employee to neighbourhood. Please try again later.',
        }),
    }, [employeeNeighbourhood]);

    const handleClearNeighbourhoods = useCallback(async (newJoiner: ResolvedNewJoiner) => {
        return message.exec(
            async () => {
                const employee = newJoiner.employee;
                if (!employee) return;
                for (const hood of employee.neighbourhoods ?? []) {
                    await removeEmployee(hood as Neighbourhood, employee)
                }
            },
            'Removed new joiner from all neighbourhoods',
            'Failed to remove new joiner from all neighbourhoods. Please try again later.'
        );
    }, [removeEmployee, message]);

    const handleChecklist = useFormDialogAction({
        title: 'Checklist Configuration',
        description: 'Define the checklist items shown for each new joiner',
        submitButtonText: 'Save',
        extraButtons: ChecklistManagementResetButton,
        fullWidth: true,
        renderForm: ChecklistManagementForm,
        action: async openDialog => {
            openDialog({
                initialValue: createNewJoinerChecklistForm(await newJoinerChecklistItems.value),
                onSubmit: form => saveNewJoinerChecklistForm(
                    form,
                    createNewJoinerChecklistItem,
                    updateNewJoinerChecklistItem,
                    deleteNewJoinerChecklistItem,
                ),
                successMessage: 'Checklist saved',
                errorMessage: 'Failed to save checklist. Please try again later.',
            });
        },
    }, [newJoinerChecklistItems, createNewJoinerChecklistItem, updateNewJoinerChecklistItem, deleteNewJoinerChecklistItem]);

    const newJoinerApi = useMemo(() => {
            return newJoinerChecklistItems.map(newJoinerChecklistItems => new NewJoinerApi(
                handleEdit,
                handleDelete,
                updateNewJoiner,
                handleSelectNeighbourhood,
                handleClearNeighbourhoods,
                newJoinerChecklistItems,
                canAdmin,
                canOperateKeys,
                canManageNeighbourhoods,
            ));
        }, [handleEdit, handleDelete, updateNewJoiner, handleSelectNeighbourhood, handleClearNeighbourhoods, newJoinerChecklistItems, canAdmin, canOperateKeys, canManageNeighbourhoods],
    );

    const filteredNewJoiners = useMemo(
        () => RemoteValue.combine({newJoinerApi, newJoiners})
            .map(({newJoinerApi, newJoiners: allNewJoiners}) => {
                const filteredNewJoiners = applyNewJoinersFilter(allNewJoiners, filter);
                return ({
                    newJoinerApi,
                    filteredNewJoiners,
                    length: filteredNewJoiners.length,
                });
            }),
        [newJoiners, filter, newJoinerApi],
    );

    return (
        <Page
            header="New Joiners"
            counter={<StorePageCount totalValue={newJoiners} value={filteredNewJoiners}/>}
            description="Track employees who are joining the office."
            filter={<NewJoinersFilter filter={filter} setFilter={setFilter}/>}
            actions={
                <Stack direction="row" gap={2}>
                    {canImport && <ImportBerlinNewJoinersButton/>}
                    {canManageChecklist && (
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon/>}
                            onClick={handleChecklist}
                        >
                            Checklist
                        </Button>
                    )}
                    {canAdmin && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon/>}
                            onClick={handleCreate}
                        >
                            Add New Joiner
                        </Button>
                    )}
                </Stack>
            }
        >
            <LoadingData
                value={filteredNewJoiners}
                totalValue={newJoiners}
                emptyMessage="No new joiners found."
                noMatchMessage="No new joiners match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load new joiners. Please try again later."
            >{({filteredNewJoiners, newJoinerApi}) => (
                <NewJoinerTable
                    rows={filteredNewJoiners}
                    api={newJoinerApi}
                />
            )}</LoadingData>
        </Page>
    );
}
